/**
 * Marketing demo use-case catalog — drives login-page Demo use cases (staging / non-prod).
 *
 * Add Ready packs under public/marketing-demos/use-cases/<slug>/ then register here.
 * See public/marketing-demos/HOW-TO-ADD-A-USE-CASE.md
 * Visibility: `isMarketingDemosPublicEnabled` — hidden on production.
 */

export const MARKETING_DEMOS_BASE_PATH = '/marketing-demos';

export const MARKETING_DEMO_STATUS = Object.freeze({
  READY: 'ready',
  PLANNED: 'planned',
});

/**
 * @typedef {object} MarketingDemoPack
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {'ready'|'planned'} status
 * @property {string} [href]
 * @property {string} [badge]
 * @property {string} [audience]
 * @property {string} [demoLength]
 * @property {string} [slug]
 */

/**
 * @typedef {object} MarketingDemoCategory
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {MarketingDemoPack[]} packs
 */

/** @type {MarketingDemoCategory[]} */
export const MARKETING_DEMO_CATEGORIES = Object.freeze([
  {
    id: 'people-comms',
    title: 'People Comms demos',
    description: 'Two separate stories: (1) hiring offer→joining path, (2) Day-1 induction platform pitch.',
    packs: [
      {
        id: 'hub',
        title: 'Use-case hub',
        description: 'Browse all ready and planned demo packs in one page.',
        status: MARKETING_DEMO_STATUS.READY,
        href: `${MARKETING_DEMOS_BASE_PATH}/hub.html`,
        badge: 'Start here',
        audience: 'Any demo',
        demoLength: '1 min',
        slug: null,
      },
      {
        id: 'hiring-15min',
        title: 'Hiring journey — offer → joining pitch',
        description:
          'Sagar path: ~2-month offer→joining engage with RM / hiring manager / India Head videos (perks + projects/tech), Day-1, pricing.',
        status: MARKETING_DEMO_STATUS.READY,
        href: `${MARKETING_DEMOS_BASE_PATH}/use-cases/hiring-induction-journey/pitch-15min.html`,
        badge: 'Primary hiring demo',
        audience: 'CHRO / Talent / Marketing',
        demoLength: '~15 min',
        slug: 'hiring-induction-journey',
      },
      {
        id: 'hiring-journey',
        title: 'Hiring journey storyboard',
        description: 'Offer → 2-month engage → Day-1 → induction storyboard for Sagar when the room wants detail.',
        status: MARKETING_DEMO_STATUS.READY,
        href: `${MARKETING_DEMOS_BASE_PATH}/use-cases/hiring-induction-journey/hiring-journey-flow.html`,
        badge: 'Storyboard',
        audience: 'Follow-up / deep dive',
        demoLength: '10–20 min',
        slug: 'hiring-induction-journey',
      },
      {
        id: 'people-comms-induction-pitch',
        title: 'Day-1 induction — capability pitch',
        description:
          'Platform / CHRO deck: personalise induction on WhatsApp at batch scale — no named hire hero, pilot framing.',
        status: MARKETING_DEMO_STATUS.READY,
        href: `${MARKETING_DEMOS_BASE_PATH}/use-cases/people-comms-induction/pitch.html`,
        badge: 'Induction platform',
        audience: 'CHRO / Talent / L&D',
        demoLength: '~15 min',
        slug: 'people-comms-induction',
      },
      {
        id: 'people-comms-pilot',
        title: '2-week pilot playbook',
        description: 'Pilot scope, success criteria, and delivery checklist.',
        status: MARKETING_DEMO_STATUS.READY,
        href: `${MARKETING_DEMOS_BASE_PATH}/use-cases/people-comms-induction/pilot-playbook.html`,
        badge: 'Playbook',
        audience: 'Buyer + delivery',
        demoLength: '5 min',
        slug: 'people-comms-induction',
      },
    ],
  },
  {
    id: 'planned',
    title: 'Planned use cases (coming soon)',
    description: 'Folders and briefs exist — add pitch HTML when marketing is ready.',
    packs: [
      {
        id: 'hr-policy-comms',
        title: 'HR policy & people updates',
        description: 'Policy / handbook / compliance journeys after induction.',
        status: MARKETING_DEMO_STATUS.PLANNED,
        badge: 'Coming soon',
        audience: 'CHRO / Internal Comms',
        demoLength: 'TBD',
        slug: 'hr-policy-comms',
      },
      {
        id: 'it-howto-enablement',
        title: 'IT how-tos & change enablement',
        description: 'Role-aware how-tos for IT rollouts (MFA, VPN, devices).',
        status: MARKETING_DEMO_STATUS.PLANNED,
        badge: 'Coming soon',
        audience: 'CIO / IT / Change',
        demoLength: 'TBD',
        slug: 'it-howto-enablement',
      },
      {
        id: 'sales-enablement',
        title: 'Sales / product enablement',
        description: 'Launch and objection enablement for field and partners.',
        status: MARKETING_DEMO_STATUS.PLANNED,
        badge: 'Coming soon',
        audience: 'Sales / Enablement',
        demoLength: 'TBD',
        slug: 'sales-enablement',
      },
    ],
  },
]);

export const MARKETING_DEMO_PACKS = Object.freeze(
  MARKETING_DEMO_CATEGORIES.flatMap((category) => category.packs)
);

export const getMarketingDemoPackById = (packId) =>
  MARKETING_DEMO_PACKS.find((pack) => pack.id === packId) || null;

export const getReadyMarketingDemoPacks = () =>
  MARKETING_DEMO_PACKS.filter((pack) => pack.status === MARKETING_DEMO_STATUS.READY);

/** Curated packs for the login marketing panel (high-level CTAs only). */
export const LOGIN_FEATURED_DEMO_PACK_IDS = Object.freeze([
  'hiring-15min',
  'people-comms-induction-pitch',
  'hub',
]);

const LOGIN_FEATURED_DEMO_ICONS = Object.freeze({
  'hiring-15min': 'mdi-account-multiple-outline',
  'people-comms-induction-pitch': 'mdi-presentation',
  hub: 'mdi-compass-outline',
});

const LOGIN_FEATURED_DEMO_SHORT_TITLES = Object.freeze({
  'hiring-15min': 'Offer → joining hire path',
  'people-comms-induction-pitch': 'Day-1 induction platform',
  hub: 'Browse all stories',
});

const LOGIN_FEATURED_DEMO_SHORT_DESCRIPTIONS = Object.freeze({
  'hiring-15min': 'Sagar story: RM / HM / India Head + perks & projects until Day-1.',
  'people-comms-induction-pitch': 'CHRO deck: batch induction on WhatsApp at scale.',
  hub: 'All ready and planned stories in one place.',
});

const LOGIN_FEATURED_DEMO_BADGES = Object.freeze({
  'hiring-15min': 'Hiring',
  'people-comms-induction-pitch': 'Induction',
  hub: 'Explore',
});

export const getLoginFeaturedMarketingDemoPacks = () =>
  LOGIN_FEATURED_DEMO_PACK_IDS.map((id) => {
    const pack = getMarketingDemoPackById(id);
    if (!pack || pack.status !== MARKETING_DEMO_STATUS.READY || !pack.href) {
      return null;
    }
    return {
      ...pack,
      icon: LOGIN_FEATURED_DEMO_ICONS[id] || 'mdi-play-circle-outline',
      shortTitle: LOGIN_FEATURED_DEMO_SHORT_TITLES[id] || pack.title,
      shortDescription: LOGIN_FEATURED_DEMO_SHORT_DESCRIPTIONS[id] || pack.description,
      badge: LOGIN_FEATURED_DEMO_BADGES[id] || pack.badge,
    };
  }).filter(Boolean);

export const getMarketingDemoCategories = () => MARKETING_DEMO_CATEGORIES;
